import React, { useEffect, useState } from 'react';
import { AlertCircle, MapPin, CheckCircle } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Button } from './button';
import locationService from '../../lib/location';

/**
 * GPSPermissionBanner
 * Requests location permission on first startup
 * Works on both mobile (Capacitor) and web (browser Geolocation API)
 */
const GPSPermissionBanner = ({ onPermissionGranted, onPermissionDenied }) => {
  const [status, setStatus] = useState('checking'); // checking, required, granted, denied
  const [error, setError] = useState(null);
  const [isWebOnly, setIsWebOnly] = useState(false);

  useEffect(() => {
    checkGPSStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkGPSStatus = async () => {
    try {
      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        const hasPermission = await locationService.hasPermission();
        if (hasPermission) {
          setStatus('granted');
          setIsWebOnly(false);
          onPermissionGranted?.();
        } else {
          setStatus('required');
          setIsWebOnly(false);
        }
        return;
      }

      // Check if browser supports Geolocation API
      if (!('geolocation' in navigator)) {
        setError('Your browser does not support location services');
        setStatus('denied');
        setIsWebOnly(true);
        return;
      }

      // Check HTTPS requirement
      if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
        setError('HTTPS is required for location services. Please use a secure connection.');
        setStatus('denied');
        setIsWebOnly(true);
        return;
      }

      const hasPermission = await locationService.hasPermission();
      if (hasPermission) {
        setStatus('granted');
        setIsWebOnly(false);
        onPermissionGranted?.();
      } else {
        setStatus('required');
        setIsWebOnly(false);
      }
    } catch (err) {
      console.error('Error checking GPS status:', err);
      setStatus('required');
    }
  };

  const handleRequestPermission = async () => {
    setStatus('checking');
    const result = await locationService.requestPermission();

    if (result.success) {
      setStatus('granted');
      setIsWebOnly(false);
      onPermissionGranted?.();
    } else {
      setStatus('denied');
      setError(result.error || 'Location permission denied. GPS is required to use this app.');
      onPermissionDenied?.();
    }
  };

  // Granted state - no banner shown
  if (status === 'granted') {
    return null;
  }

  // Denied state - blocking banner
  if (status === 'denied') {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-[#1A1A1A] border border-red-500/30 rounded-lg p-6 max-w-md w-full">
          <div className="flex items-start gap-3 mb-4">
            <AlertCircle className="text-red-500 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="text-white font-semibold text-lg">
                {isWebOnly ? 'Browser Requirements' : 'GPS Required'}
              </h3>
              <p className="text-[#A1A1AA] text-sm mt-1">
                {isWebOnly 
                  ? error
                  : 'High-precision GPS is mandatory to use this app. It verifies you\'re at the correct venue for audits and compliance.'}
              </p>
            </div>
          </div>

          {!isWebOnly && (
            <div className="bg-red-500/10 border border-red-500/20 rounded p-3 mb-4">
              <p className="text-red-400 text-xs">
                <strong>Why?</strong> GPS timestamps are attached to all submissions for accountability and verification.
              </p>
            </div>
          )}

          <div className="flex gap-3">
            {!isWebOnly && (
              <Button 
                onClick={handleRequestPermission}
                className="flex-1 bg-[#FF6600] hover:bg-[#FF8533] text-white"
              >
                Enable GPS
              </Button>
            )}
            <Button 
              onClick={() => window.location.reload()}
              variant="outline"
              className={isWebOnly ? 'w-full' : 'flex-1'}
            >
              {isWebOnly ? 'Close' : 'Retry'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Required state - request banner
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-[#FF6600]/30 rounded-lg p-6 max-w-md w-full">
        <div className="flex items-start gap-3 mb-4">
          <MapPin className="text-[#FF6600] flex-shrink-0 mt-1" size={24} />
          <div>
            <h3 className="text-white font-semibold text-lg">GPS Permission Required</h3>
            <p className="text-[#A1A1AA] text-sm mt-1">
              This app requires high-precision GPS to verify your location at job sites.
            </p>
          </div>
        </div>

        <div className="bg-[#FF6600]/10 border border-[#FF6600]/20 rounded p-3 mb-4">
          <p className="text-[#FF8533] text-xs">
            <strong>Location Usage:</strong> GPS coordinates are recorded with every checklist and audit submission for accountability and location verification.
          </p>
        </div>

        <Button 
          onClick={handleRequestPermission}
          disabled={status === 'checking'}
          className="w-full bg-[#FF6600] hover:bg-[#FF8533] text-white"
        >
          {status === 'checking' ? 'Requesting GPS Access...' : 'Enable GPS Access'}
        </Button>

        <p className="text-[#696969] text-xs text-center mt-4">
          You can manage permissions in Settings
        </p>
      </div>
    </div>
  );
};

export default GPSPermissionBanner;
